import React, { useState } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import { Product } from '../../types.ts';
import {
  ShoppingBag,
  MessageCircle,
  Tag,
  CheckCircle2,
  ChevronRight,
  ChevronDown,
  ExternalLink,
  Film,
  Grid,
  List,
  Star,
  MapPin,
  ShieldCheck,
  Store,
  Flame,
  ArrowRight,
} from 'lucide-react';

interface FeaturedProductsProps {
  badge?: string;
  title?: string;
  subtitle?: string;
  buttonText?: string;
  buttonEnabled?: boolean;
  limit?: number;
  isPageMode?: boolean;
}

export default function FeaturedProducts({
  badge = 'ENTERPRISE SOLUTIONS',
  title = 'B2B Climate Solutions & Green Services',
  subtitle = 'Scalable sustainability services, carbon accounting, renewable energy engineering, and certified green talent placement',
  buttonText = 'View All Services & Solutions →',
  buttonEnabled = true,
  limit = 8,
  isPageMode = false,
}: FeaturedProductsProps) {
  const { products, settings, setSelectedProduct, navigate } = useApp();
  const [filterCat, setFilterCat] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [visibleCount, setVisibleCount] = useState<number>(limit || 8);

  const primaryColor = settings?.theme.primaryColor || '#1B4332';

  // Get distinct categories
  const categories = ['all', ...Array.from(new Set(products.map((p) => p.category)))];

  const filtered = products.filter((p) => filterCat === 'all' || p.category === filterCat);
  const visibleProducts = isPageMode ? filtered.slice(0, visibleCount) : filtered.slice(0, limit);

  const formatRupiah = (val: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(val);
  };

  const getWhatsAppLink = (prod: Product) => {
    const rawNumber = settings?.contact?.whatsappNumber || settings?.contactWidget?.whatsappNumber || '6281220008890';
    const waNumber = rawNumber.replace(/[^0-9]/g, '');
    const siteName = settings?.siteName || 'Greenjobs ITB';
    if (prod.whatsappMessage && prod.whatsappMessage.trim()) {
      return `https://wa.me/${waNumber}?text=${encodeURIComponent(prod.whatsappMessage.trim())}`;
    }
    const priceText = prod.hidePrice ? '' : ` (${formatRupiah(prod.price)})`;
    const defaultMsg = `Hello ${siteName} Advisory, I am interested in consulting regarding "${prod.title}"${priceText}. Please provide proposal and availability details.`;
    return `https://wa.me/${waNumber}?text=${encodeURIComponent(defaultMsg)}`;
  };

  return (
    <section id="featured-products-section" className="py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: primaryColor }}
              />
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                {badge}
              </span>
            </div>
            <h2
              className={`text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight inline-flex items-center gap-2.5 ${
                !isPageMode ? 'cursor-pointer group hover:text-emerald-700 transition-colors' : ''
              }`}
              onClick={() => !isPageMode && navigate('products')}
            >
              <span>{title}</span>
              {!isPageMode && (
                <span
                  className="inline-flex items-center justify-center w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-emerald-50 text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition-all shadow-2xs group-hover:translate-x-1 shrink-0"
                  title="View All Services & Solutions"
                >
                  <ArrowRight className="w-4 h-4 sm:w-4.5 sm:h-4.5" />
                </span>
              )}
            </h2>
            {subtitle && <p className="text-sm text-slate-500 mt-1.5 max-w-2xl">{subtitle}</p>}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {/* View Mode Switcher (Grid vs List) */}
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-xl border border-slate-200">
              <button
                type="button"
                onClick={() => setViewMode('grid')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                  viewMode === 'grid'
                    ? 'bg-emerald-700 text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                title="Grid View"
              >
                <Grid className="w-3.5 h-3.5" />
                <span>Grid</span>
              </button>
              <button
                type="button"
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 ${
                  viewMode === 'list'
                    ? 'bg-emerald-700 text-white shadow-xs'
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                title="List View"
              >
                <List className="w-3.5 h-3.5" />
                <span>List</span>
              </button>
            </div>

            {/* Category Filter Pills */}
            <div className="flex flex-wrap gap-1.5 bg-slate-100 p-1 rounded-xl">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    setFilterCat(cat);
                    setVisibleCount(limit || 8);
                  }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                    filterCat === cat
                      ? 'bg-white text-slate-900 shadow-xs'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  {cat === 'all' ? 'All' : cat}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Product Cards Rendering (Grid vs List - Clean Tokopedia Style) */}
        {viewMode === 'list' ? (
          <div className="space-y-3">
            {visibleProducts.map((prod) => (
              <div
                key={prod.id}
                className="bg-white rounded-xl border border-slate-200/90 overflow-hidden shadow-2xs hover:shadow-md transition-all p-2.5 sm:p-3.5 flex flex-row gap-3 sm:gap-4 items-center group"
              >
                {/* Product Cover Image */}
                <div
                  className="relative w-20 h-20 sm:w-28 sm:h-28 rounded-lg overflow-hidden bg-slate-100 shrink-0 cursor-pointer"
                  onClick={() => navigate('product-detail', prod.slug || prod.id)}
                >
                  <img
                    src={prod.image || 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=800&q=80'}
                    alt={prod.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {prod.badge && (
                    <span
                      className="absolute top-1 left-1 px-1.5 py-0.5 text-white text-[9px] font-extrabold rounded-md uppercase tracking-tight shadow-xs flex items-center gap-0.5"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <Star className="w-2.5 h-2.5 fill-amber-300 text-amber-300 shrink-0" />
                      <span>{prod.badge}</span>
                    </span>
                  )}
                </div>

                {/* Main Product Info */}
                <div className="flex-1 min-w-0 space-y-1">
                  <h3
                    onClick={() => navigate('product-detail', prod.slug || prod.id)}
                    className="font-bold text-slate-900 text-xs sm:text-sm leading-snug hover:text-emerald-700 cursor-pointer transition-colors"
                  >
                    {prod.title}
                  </h3>

                  <div className="flex items-center gap-2 pt-0.5">
                    {prod.hidePrice ? (
                      <div
                        className="text-xs sm:text-sm font-extrabold"
                        style={{ color: primaryColor }}
                      >
                        {prod.priceCustomLabel || 'Inquire Advisory'}
                      </div>
                    ) : (
                      <>
                        <div
                          className="text-sm sm:text-base font-extrabold"
                          style={{ color: primaryColor }}
                        >
                          {formatRupiah(prod.price)}
                        </div>
                        {prod.originalPrice && prod.originalPrice > prod.price && (
                          <span className="text-[10px] sm:text-xs text-slate-400 line-through">
                            {formatRupiah(prod.originalPrice)}
                          </span>
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Compact Action Buttons */}
                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1.5 shrink-0 pl-2 border-l border-slate-100">
                  <button
                    onClick={() => navigate('product-detail', prod.slug || prod.id)}
                    className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-800 text-xs font-bold rounded-lg transition-colors cursor-pointer"
                  >
                    Details
                  </button>
                  <a
                    href={getWhatsAppLink(prod)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-800 text-white text-xs font-bold rounded-lg flex items-center gap-1 shadow-2xs hover:shadow-xs transition-all cursor-pointer"
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    <span>{prod.orderButtonText || settings?.defaultProductCtaText || 'Inquire'}</span>
                  </a>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
            {visibleProducts.map((prod) => (
              <div
                key={prod.id}
                className="bg-white rounded-xl border border-slate-200/90 overflow-hidden shadow-2xs hover:shadow-md transition-all duration-200 flex flex-col group"
              >
                {/* Square Cover Image */}
                <div
                  className="relative aspect-square overflow-hidden bg-slate-100 cursor-pointer"
                  onClick={() => navigate('product-detail', prod.slug || prod.id)}
                >
                  <img
                    src={prod.image || 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?auto=format&fit=crop&w=800&q=80'}
                    alt={prod.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  {prod.badge && (
                    <div
                      className="absolute top-2 left-2 px-2 py-0.5 rounded-md text-[9px] font-extrabold uppercase tracking-tight text-white shadow-xs flex items-center gap-0.5"
                      style={{ backgroundColor: primaryColor }}
                    >
                      <Star className="w-2.5 h-2.5 fill-amber-300 text-amber-300 shrink-0" />
                      <span>{prod.badge}</span>
                    </div>
                  )}
                </div>

                {/* Card Body */}
                <div className="p-2.5 sm:p-3 flex-1 flex flex-col justify-between">
                  <div>
                    <h3
                      onClick={() => navigate('product-detail', prod.slug || prod.id)}
                      className="font-semibold text-slate-900 text-xs sm:text-sm leading-snug hover:text-emerald-700 cursor-pointer transition-colors"
                    >
                      {prod.title}
                    </h3>

                    {/* Price Tag */}
                    <div className="mt-1.5">
                      {prod.hidePrice ? (
                        <span
                          className="text-xs sm:text-sm font-extrabold tracking-tight"
                          style={{ color: primaryColor }}
                        >
                          {prod.priceCustomLabel || 'Inquire Advisory'}
                        </span>
                      ) : (
                        <div>
                          <span
                            className="text-sm sm:text-base font-extrabold tracking-tight block"
                            style={{ color: primaryColor }}
                          >
                            {formatRupiah(prod.price)}
                          </span>
                          {prod.originalPrice && prod.originalPrice > prod.price && (
                            <span className="text-[10px] text-slate-400 line-through">
                              {formatRupiah(prod.originalPrice)}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-2.5 pt-2 border-t border-slate-100">
                    <div className="grid grid-cols-2 gap-1">
                      <button
                        type="button"
                        onClick={() => navigate('product-detail', prod.slug || prod.id)}
                        className="py-1 px-1.5 rounded-lg border border-slate-200 text-slate-700 bg-slate-50 hover:bg-slate-100 text-[11px] font-bold transition-all text-center cursor-pointer"
                      >
                        Details
                      </button>

                      <a
                        id={`prod-wa-btn-${prod.id}`}
                        href={getWhatsAppLink(prod)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="py-1 px-1.5 rounded-lg text-white text-[11px] font-bold shadow-2xs hover:opacity-90 transition-all flex items-center justify-center gap-0.5 text-center cursor-pointer"
                        style={{ backgroundColor: primaryColor }}
                      >
                        <MessageCircle className="w-3 h-3" />
                        <span>{prod.orderButtonText || settings?.defaultProductCtaText || 'Inquire'}</span>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Bottom Actions - More button only in Page Mode */}
        {isPageMode && filtered.length > visibleCount && (
          <div className="mt-10 text-center">
            <button
              type="button"
              id="products-more-btn"
              onClick={() => setVisibleCount((prev) => prev + (limit || 8))}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl text-white font-bold text-xs sm:text-sm shadow-md hover:shadow-lg hover:opacity-95 active:scale-98 transition-all cursor-pointer"
              style={{ backgroundColor: primaryColor }}
            >
              <ChevronDown className="w-4 h-4 animate-bounce" />
              <span>Load More Solutions ({filtered.length - visibleCount} Remaining)</span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
